import React, { useContext, useState } from "react"
import { Checkbox, Grid } from '@mui/material'
import { ExistingCard } from "../components/ExistingCard";
import { CardTypes, BoardSchemeInterface, ProjectSchemeInterface, TeamsInterface, DesignFirmType } from "ts-interfaces";
import RenameBoardModal from "./RenameBoardModal";
import { DeleteComponentModal } from "../components/DeleteComponentModal";
import { associateBoardWithProject, copyBoard, renameBoard, deleteBoard  } from "../../v2/apis/boardsSchemeApi";
import { PreviewDialog } from "../../buttons/ShareButton";
import { AssociatedProjectModal } from "../components/AssociatedProjectModal";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../auth/Auth";
import { useMattoState } from "../../MattoState";
import { usePermissions, firebaseHelper } from "ui";
import { useProjectSchemas } from "../../global-state/useProjectSchemas";
import { useBoards } from "../../global-state/useBoards";

interface DisplayBoardsProps {
    data: BoardSchemeInterface[];
    onBoardUpdate: (board: BoardSchemeInterface, addBoard?: boolean) => void;
    designFirms: DesignFirmType[]; 
    teamMembers: TeamsInterface[]; 
    cardSize?: string; //use union type like "default" | "small" | "large" if you have specific sizes
    showCheckboxes?: boolean;
    selectedBoardIds?: Set<string>;
    selectedProjectId?: string;
    allProjects?: ProjectSchemeInterface[];
    onBoardSelectionChange?: (id: string, checked: boolean) => void;
    hasViewerAccess?: boolean;
}

export const DisplayBoards = ({ 
    data, 
    onBoardUpdate, 
    designFirms, 
    teamMembers, 
    cardSize="default",
    showCheckboxes = false,
    selectedBoardIds = new Set(),
    selectedProjectId="",
    allProjects = [],
    onBoardSelectionChange = (id: string, checked: boolean) => {},
    hasViewerAccess
}: DisplayBoardsProps) => {
    const {currentUser} = useContext(AuthContext);
    const [selectedBoard, setSelectedBoard] = useState<BoardSchemeInterface | null>(null)
    const [showRenameModal, setShowRenameModal] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [showShareModal, setShowShareModal] = useState(false)
    const [showAssociatedProjectModal, setShowAssociatedProjectModal] = useState(false)
    const [shareUrl, setShareUrl] = useState<string>('')
    const { permissions, hasBoardAccess, hasProjectAccess } = usePermissions(currentUser?.uid, firebaseHelper.db);
    const setSnackbarMessage = useMattoState((state) => state.setSnackbarMessage);
    const history = useNavigate();
    const { getProjectOwner } = useProjectSchemas(state => ({ getProjectOwner: state.getProjectOwner }));

    if (!data || data.length === 0) return null;

    const handleClick = (board: BoardSchemeInterface) => { 
        if (!showCheckboxes) {
            const projectOwner = board.associatedProjectId ? getProjectOwner(board.associatedProjectId) : undefined;
            const hasProjectEditAccess = board.associatedProjectId ? hasProjectAccess(board.associatedProjectId, 'write', projectOwner) : false;
            const hasBoardEditAccess = hasBoardAccess(board.id, 'write', board.owner || board.user);
            if (hasBoardEditAccess || hasProjectEditAccess) {
                history("/project/" + (board?.id || '')); 
            } else {
                history("/show/" + (board?.id || ''));
            }
        }
    }

    const handleCheckboxChange = (boardId: string, event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();
        onBoardSelectionChange(boardId, event.target.checked);
    };
    
    const openDeleteModal = (e,board) => {
        e.stopPropagation()
        setSelectedBoard(board)
        setShowDeleteModal(true)
    }

    const handleDelete = async () => {
        try {
            if(!selectedBoard) return;    
            setSnackbarMessage('Deleting board...');    
            await deleteBoard(selectedBoard.id, currentUser.uid, permissions);
            await associateBoardWithProject(selectedBoard.id, null, currentUser.uid, permissions);
            selectedBoard.archive = true;
            onBoardUpdate?.(selectedBoard);
            setSnackbarMessage('Board deleted successfully');
        }
        finally { setShowDeleteModal(false) }
    }

    const openRenameModal = (e,board) => {
        e.stopPropagation()
        setSelectedBoard(board)
        setShowRenameModal(true)
    }

    const handleRename = async (newName) => {
        if(!selectedBoard) return;
        await renameBoard(selectedBoard.id, newName, currentUser.uid, permissions);
        selectedBoard.projectData.title = newName;
        onBoardUpdate?.(selectedBoard);
        setSnackbarMessage('Board renamed successfully');
    }

    const handleCopy = async (e,board) => {
        e.stopPropagation();
        setSnackbarMessage('Duplicating board...');
        setSelectedBoard(board);
        const copiedBoard = await copyBoard(board, currentUser.uid, permissions);
        
        // Only add to current list if the duplicated board belongs here
        // In BoardsPanel (cardSize='small'), we're showing boards for a specific project,
        // so we shouldn't add the duplicated board since it now has no associatedProjectId
        // In BoardScheme (cardSize='default'), we're showing user's own boards,
        // so we should add it since it's now owned by the current user
        const shouldAddToCurrentList = cardSize !== 'small';
        
        if (shouldAddToCurrentList) {
            onBoardUpdate?.(copiedBoard, true);
        } else {
            // If not adding to current list, still need to add to global state
            // This happens when duplicating from BoardsPanel (project context)
            useBoards.getState().setBoards([copiedBoard, ...useBoards.getState().boards]);
        }
        
        setSnackbarMessage('Board duplicated successfully');
    }

    const openShareModal = (e,board) => {
        e.stopPropagation()
        setSelectedBoard(board)
        const projectId = board?.projectData?.projectId;
        if(!projectId) return;
        const shareUrl = `${window.location.origin}/show/${projectId}`;
        setShareUrl(shareUrl)
        setShowShareModal(true)
    }

    const openAssociatedProjectModal = (e,board) => {
        e.stopPropagation()
        setSelectedBoard(board)
        setShowAssociatedProjectModal(true)
    }

    const handleAssociatedProjectChange = async ({ newProjectId, oldProjectId, board=null }) => {
        const targetBoard = board || selectedBoard; // fallback to selectedBoard if board not provided
    
        if(!targetBoard) return;
        try {
            await associateBoardWithProject(targetBoard.id, newProjectId, currentUser.uid, permissions);
            
            const updatedBoard = {
                ...targetBoard,
                associatedProjectId: newProjectId,
                projectData: {
                    ...targetBoard.projectData, // keep other projectData fields
                    associatedProjectId: newProjectId // update nested field
                },
                updatedAt: Date.now()
            };
            
            // If moving from one project to another
            if (oldProjectId && oldProjectId !== newProjectId) {
                useProjectSchemas.getState().removeBoardFromProjects(targetBoard.id);
            }
            
            // Add to new project
            if (newProjectId) {
                useProjectSchemas.getState().updateBoardInProjects(updatedBoard);
            }
            
            // Update board store
            useBoards.getState().setBoards(
                useBoards.getState().boards.map(b => b.id === targetBoard.id ? updatedBoard : b)
            );
            setShowAssociatedProjectModal(false);
            setSnackbarMessage('Board association updated successfully');
        } catch (error) {
            console.error('Error updating board association:', error);
            setSnackbarMessage(error.message || 'Failed to update board association');
        }
    };

    const handleRemoveFromProject = (board) => {
        setSnackbarMessage('Removing board from project...');
        setSelectedBoard(board);
        handleAssociatedProjectChange({
            newProjectId: null,
            oldProjectId: board?.associatedProjectId || null,
            board: board
        });
    };

    return (
    <>
        {data.map((board: BoardSchemeInterface) => {
            const projectOwner = board.associatedProjectId ? getProjectOwner(board.associatedProjectId) : undefined;
            const hasProjectReadAccess = board.associatedProjectId ? hasProjectAccess(board.associatedProjectId, 'read', projectOwner) : false;
            const hasBoardReadAccess = hasBoardAccess(board.id, 'read', board.owner || board.user);
            // check if allProjects is provided and if the board's associated project is in the list
            const isProjectAssociated = allProjects.some(project => project.id === board.associatedProjectId);
            const isDisabled = !!(showCheckboxes && selectedProjectId && board.associatedProjectId && isProjectAssociated && (board.associatedProjectId !== selectedProjectId));
            
            const hasBoardWriteAccess = hasBoardAccess(board.id, 'write', board.owner || board.user);
            // Use prop-level hasViewerAccess if provided, otherwise calculate it individually
            const computedHasViewerAccess = hasViewerAccess !== undefined ? hasViewerAccess : 
                (hasProjectReadAccess || hasBoardReadAccess) && !hasBoardWriteAccess;

            return (
                <Grid 
                    key={board.id} 
                    item 
                    sx={{ position: 'relative' }}>
                    {showCheckboxes && (
                        <Checkbox
                            checked={selectedBoardIds.has(board.id)}
                            onChange={(e) => handleCheckboxChange(board.id, e)}
                            disabled={isDisabled}
                            aria-label={board.projectData?.title || 'Board Checkbox'}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                zIndex: 1,
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                borderRadius: '4px',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                }
                            }}
                        />
                    )}
                    <ExistingCard 
                        data={{
                            ...board,
                            name: board.projectData?.title,
                            thumbnail: board.files?.screenshot,
                            updatedAt: board.updatedAt
                        }}
                        type={CardTypes.Boards}
                        designFirms={designFirms}
                        teamMembers={teamMembers}
                        onClick={() => !isDisabled && handleClick(board) } 
                        onDelete={(e) => !isDisabled && openDeleteModal(e,board)} 
                        onRename={(e) => !isDisabled && openRenameModal(e, board)} 
                        onCopy={(e) => !isDisabled && handleCopy(e, board)} 
                        onShare={(e) => !isDisabled && openShareModal(e, board)}
                        onAssociatedProjectChange={(e) => !isDisabled && openAssociatedProjectModal(e, board)}
                        onRemoveFromProject={() => !isDisabled && handleRemoveFromProject(board)}
                        cardSize={cardSize}
                        hasViewerAccess={computedHasViewerAccess}
                        isDisabled={isDisabled}
                    />
                </Grid>
            )}
        )}
        <RenameBoardModal name={selectedBoard?.projectData?.title} open={showRenameModal} setOpen={setShowRenameModal} onSave={handleRename}  />
        <DeleteComponentModal open={showDeleteModal} setOpen={setShowDeleteModal} onDelete={handleDelete} />
        <PreviewDialog open={showShareModal} onClose={() => setShowShareModal(false)} shareUrl={shareUrl} title={selectedBoard?.projectData?.title} />
        <AssociatedProjectModal open={showAssociatedProjectModal} onClose={() => setShowAssociatedProjectModal(false)}  onSave={handleAssociatedProjectChange} data={selectedBoard} type={CardTypes.Boards} /> 
    </>
    );

}